import { useCallback, useEffect, useState } from "react";
import { normalizePropertyNames } from "@cayo/admin-components/components/grids/utils";
import { apiMocks } from "@cayo/core/admin-components/grids/SpnGrid/apiMocks";
import { ISpnGridItem } from "@cayo/core/admin-components/grids/SpnGrid/types";
import {
  groupExpandedAndCollapsedByKey,
  groupParsedSpns,
} from "@cayo/core/admin-components/grids/SpnGrid/utils";
import ajax from "@cayo/shared/libs/ajax";
import useService from "@cayo/shared/services/hooks/useService";
import { ajaxUtils } from "@cayo/shared/utils/ajax";

export const useSelectServicesDialog = (cayoObjId: string) => {
  const [services, setServices] = useState<ISpnGridItem[]>([]);
  const [selectedServices, setSelectedServices] = useState<ISpnGridItem[]>([]);
  const [collapsedKeyValue, setCollapsedKeyValue] = useState<{ [key: string]: ISpnGridItem[] }>({});
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(cayoObjId));

  const actionBuilder = useService("actionBuilder");
  const { uiInteraction } = actionBuilder;

  useEffect(() => {
    if (!cayoObjId) {
      return;
    }

    const fetchServices = async () => {
      const scope = apiMocks.getServicesSearchScope(cayoObjId)[0];
      try {
        setIsLoading(true);
        uiInteraction.setLoading(true);
        const result = await ajax.getClient(scope.path, "POST", undefined, scope.body);
        if (result) {
          const expandedResult = normalizePropertyNames(result.commandExecutionResultSet);
          const collapsedResult = groupParsedSpns(expandedResult);
          setServices(collapsedResult ?? []);
          setCollapsedKeyValue(groupExpandedAndCollapsedByKey(expandedResult, collapsedResult));
        }
      } catch (e) {
        return ajaxUtils.handleError(e);
      } finally {
        uiInteraction.setLoading(false);
        setIsLoading(false);
      }
    };

    fetchServices();
  }, [cayoObjId]);

  const resolveExpandedFromSelected = useCallback(
    (selected: ISpnGridItem[]): ISpnGridItem[] => {
      const expanded: ISpnGridItem[] = [];
      for (const s of selected) {
        const children = collapsedKeyValue[s.originalSPN];
        if (children) {
          expanded.push(...children);
        }
      }
      return expanded;
    },
    [collapsedKeyValue]
  );

  return {
    services,
    isLoading,
    selectedServices,
    setSelectedServices,
    columns: apiMocks.servicesColumns,
    resolveExpandedFromSelected,
  };
};
